import React, { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  AtSign, ArrowRight, Search, Plus, Download,
  LayoutGrid, List, Star, Slash, RefreshCw, Edit2, X, Check,
  Users, FilterX, AlertCircle, Lock, Unlock
} from 'lucide-react';
import { getModelByUsername, getPromoteSummary, patchFlags, patchModel, batchUpdateFlags } from '../api/models';
import { getActiveSource, setActiveSource, AdminActiveSourceView } from '../api/activeSource';
import { AdminModelView, AdminPromoteSummary } from '../api/types';
import { pickImageUrl } from '../utils/imageUrls';
import PageHeader from '../components/shared/PageHeader';
import ModelEditPage from './ModelEditPage';
import CreateModelForm from '../components/CreateModelForm';
import SourceToggle from '../components/SourceToggle';
import { SkelKpi } from '../components/shared/Skeleton';
import { EmptyState } from '../components/shared/EmptyState';
import { TableSkeleton, CardSkeleton } from '../components/shared/TableSkeleton';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { useConfirm } from '../components/shared/ConfirmDialog';
import { useUrlState } from '../hooks/useUrlState';

// A card can represent a dual-source (OF+OT) username collapsed to one tile — siblingEntry
// carries the other row so the toggle/lock can act on either id without a second card.
type DisplayModel = AdminModelView & { siblingEntry?: AdminModelView };

type MonetizationType = 'all' | 'PPC' | 'CPL' | 'REVSHARE';
type SourceFilterType = 'all' | 'OT' | 'OF';
type GenderFilterType = 'all' | 'trans' | 'straight';
type StateChip = 'active' | 'blacklist' | 'noimg';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatRelativeTime(value?: string): string {
  if (!value) return '—';
  try {
    const date = new Date(value);
    const diff = Date.now() - date.getTime();
    if (diff < 0) return 'now';
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    if (days < 30) return `${Math.floor(days / 7)}w`;
    return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short' }).format(date);
  } catch {
    return '—';
  }
}

const fmt = (n: number | null | undefined) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US').format(n);
};

const ModelsListPage: React.FC = () => {
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  // --- Pinpoint search ---
  const [pinpointUsername, setPinpointUsername] = useState('');
  const [pinpointSearching, setPinpointSearching] = useState(false);
  const [pinpointError, setPinpointError] = useState<string | null>(null);

  // --- Source data ---
  const [promoteSummary, setPromoteSummary] = useState<AdminPromoteSummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // --- Filters / UI state (key filters persisted in URL — shareable) ---
  const [urlState, setUrlState] = useUrlState({
    q: '',
    src: 'all' as SourceFilterType,
    mon: 'all' as MonetizationType,
    gender: 'all' as GenderFilterType,
    sort: 'likes-desc' as 'likes-desc' | 'likes-asc' | 'username-asc',
    view: 'grid' as 'grid' | 'table'
  });
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.q);
  const debouncedSearch = useDebouncedValue(localSearchTerm, 250);
  useEffect(() => {
    if (debouncedSearch !== urlState.q) setUrlState({ q: debouncedSearch });
  }, [debouncedSearch]);  // eslint-disable-line react-hooks/exhaustive-deps
  const sourceFilter = urlState.src;
  const setSourceFilter = (v: SourceFilterType) => setUrlState({ src: v });
  const monetizationFilter = urlState.mon;
  const setMonetizationFilter = (v: MonetizationType) => setUrlState({ mon: v });
  const genderFilter = urlState.gender;
  const setGenderFilter = (v: GenderFilterType) => setUrlState({ gender: v });
  const [stateChips, setStateChips] = useState<Set<StateChip>>(new Set(['active']));
  const sortOption = urlState.sort;
  const setSortOption = (v: 'likes-desc' | 'likes-asc' | 'username-asc') => setUrlState({ sort: v });
  const viewMode = urlState.view;
  const setViewMode = (v: 'grid' | 'table') => setUrlState({ view: v });

  // --- Selection ---
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // --- Edit modal ---
  const [editingModelId, setEditingModelId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // --- Virtual scroll ---
  const ITEMS_PER_PAGE = 60;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // ---- Data load ----
  const fetchPromoteSummary = useCallback(async () => {
    setIsSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await getPromoteSummary();
      setPromoteSummary(data);
    } catch (e) {
      console.error(e);
      setSummaryError('Failed to load promote summary.');
    } finally {
      setIsSummaryLoading(false);
    }
  }, []);

  useEffect(() => { void fetchPromoteSummary(); }, [fetchPromoteSummary]);

  useEffect(() => { setVisibleCount(ITEMS_PER_PAGE); },
    [debouncedSearch, sourceFilter, monetizationFilter, genderFilter, stateChips, sortOption]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(es => {
      if (es[0].isIntersecting) setVisibleCount(c => c + ITEMS_PER_PAGE);
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ---- Pinpoint search ----
  const runPinpoint = async () => {
    const u = pinpointUsername.trim();
    if (!u || pinpointSearching) return;
    setPinpointSearching(true);
    setPinpointError(null);
    try {
      // Open the row the public creator page actually serves: the by-username lookup
      // prefers the OF row, so when the active-source override says OT, ask for OT
      // explicitly. getActiveSource failures are swallowed — it only refines the pick.
      let source: 'OF' | 'OT' | undefined;
      try {
        if ((await getActiveSource(u)).source === 'OT') source = 'OT';
      } catch { /* fall back to the default OF-preferring lookup */ }
      let model: AdminModelView;
      try {
        model = await getModelByUsername(u, source);
      } catch (e) {
        if (!source) throw e;
        // Stale OT override without a live OT row — retry the default lookup.
        model = await getModelByUsername(u);
      }
      setEditingModelId(model.id);
    } catch {
      setPinpointError(`No model with @${u}`);
    } finally {
      setPinpointSearching(false);
    }
  };

  // ---- Derived ----
  const getPreviewUrl = (m: AdminModelView) => pickImageUrl(m.imageUrls, m.imageFilename);
  const hasPreview = (m: AdminModelView) => Boolean(getPreviewUrl(m));

  const monCounts = useMemo(() => {
    if (!promoteSummary) return { PPC: 0, CPL: 0, REVSHARE: 0 };
    const ms = promoteSummary.models;
    return {
      PPC: ms.filter(m => m.monetizationGroup === 'PPC').length,
      CPL: ms.filter(m => m.monetizationGroup === 'CPL').length,
      REVSHARE: ms.filter(m => m.monetizationGroup === 'REVSHARE').length,
    };
  }, [promoteSummary]);

  const sourceCounts = useMemo(() => {
    if (!promoteSummary) return { OT: 0, OF: 0 };
    return {
      OT: promoteSummary.models.filter(m => m.source === 'OT').length,
      OF: promoteSummary.models.filter(m => m.source === 'OF').length,
    };
  }, [promoteSummary]);

  const genderCounts = useMemo(() => {
    if (!promoteSummary) return { trans: 0, straight: 0 };
    const trans = promoteSummary.models.filter(m => m.tags?.includes('trans')).length;
    return { trans, straight: promoteSummary.models.length - trans };
  }, [promoteSummary]);

  const noImgCount = useMemo(
    () => promoteSummary ? promoteSummary.models.filter(m => !hasPreview(m)).length : 0,
    [promoteSummary]
  );

  const promotedActiveMatrix = useMemo(() => {
    const empty = {
      OT: { PPC: 0, CPL: 0, REVSHARE: 0, total: 0 },
      OF: { PPC: 0, CPL: 0, REVSHARE: 0, total: 0 },
    };
    if (!promoteSummary) return empty;
    for (const m of promoteSummary.models) {
      if (m.blacklisted) continue;
      const src = m.source === 'OT' ? 'OT' : m.source === 'OF' ? 'OF' : null;
      if (!src) continue;
      const mon = m.monetizationGroup;
      if (mon === 'PPC' || mon === 'CPL' || mon === 'REVSHARE') {
        empty[src][mon]++;
        empty[src].total++;
      }
    }
    return empty;
  }, [promoteSummary]);

  const blacklistCount = promoteSummary?.totalPromoteBlacklisted ?? 0;
  const totalModels = promoteSummary?.models.length ?? 0;
  const totalPromote = promoteSummary?.totalPromote ?? 0;

  // ---- Dual-source (OF+OT) collapse: one card per username instead of one per row ----
  const groupedByUsername = useMemo(() => {
    const map = new Map<string, AdminModelView[]>();
    if (!promoteSummary) return map;
    for (const m of promoteSummary.models) {
      // Only OF/OT rows may collapse into a dual-source pair. Legacy rows (NULL or any
      // other source) get a per-row key so they always stay their own card — otherwise a
      // username with OF + OT + legacy rows would hide the third row behind the pair.
      const key = m.source === 'OF' || m.source === 'OT'
        ? m.modelUsername.toLowerCase()
        : `#row:${m.id}`;
      const arr = map.get(key);
      if (arr) arr.push(m); else map.set(key, [m]);
    }
    return map;
  }, [promoteSummary]);

  const dualSourceUsernames = useMemo(
    () => Array.from(groupedByUsername.entries())
      .filter(([, rows]) => rows.length === 2)
      .map(([username]) => username),
    [groupedByUsername]
  );

  // Same override the /cpc/of-ot page and ModelEditPage read/write — reused here so the
  // toggle on a card is the single source of truth, not a page-local preference.
  const activeSourceQueries = useQueries({
    queries: dualSourceUsernames.map(username => ({
      queryKey: ['active-source', username],
      queryFn: () => getActiveSource(username),
      staleTime: 60_000,
    })),
  });

  const activeSourceByUsername = useMemo(() => {
    const map = new Map<string, 'OF' | 'OT'>();
    dualSourceUsernames.forEach((username, i) => {
      map.set(username, activeSourceQueries[i]?.data?.source ?? 'OF');
    });
    return map;
  }, [dualSourceUsernames, activeSourceQueries]);

  const displayModels = useMemo<DisplayModel[]>(() => {
    const out: DisplayModel[] = [];
    for (const rows of groupedByUsername.values()) {
      if (rows.length === 1) { out.push(rows[0]); continue; }
      const active = activeSourceByUsername.get(rows[0].modelUsername.toLowerCase()) ?? 'OF';
      let primary = rows.find(r => r.source === active) ?? rows[0];
      // The Source filter must reach BOTH rows of a dual-source username: if only the
      // non-active row matches the selected source, display that row instead (the active
      // one moves into siblingEntry) — otherwise e.g. the OF row of an OT-active username
      // would be unfindable under the "OF" filter. With filter 'all' primary stays the
      // active source, as before.
      if (sourceFilter !== 'all' && primary.source !== sourceFilter) {
        const filterMatch = rows.find(r => r.source === sourceFilter);
        if (filterMatch) primary = filterMatch;
      }
      const sibling = rows.find(r => r.id !== primary.id);
      out.push({ ...primary, siblingEntry: sibling });
    }
    return out;
  }, [groupedByUsername, activeSourceByUsername, sourceFilter]);

  const [pendingSourceUsername, setPendingSourceUsername] = useState<string | null>(null);
  const handleSetActive = async (username: string, source: 'OF' | 'OT') => {
    setPendingSourceUsername(username);
    try {
      await setActiveSource(username, source);
      // Cache keys are lowercased (see groupedByUsername / useQueries above) — write under
      // the same casing or the toggle never re-renders for mixed-case usernames.
      queryClient.setQueryData<AdminActiveSourceView>(['active-source', username.toLowerCase()], { username, source });
    } finally {
      setPendingSourceUsername(null);
    }
  };

  const [pendingLockId, setPendingLockId] = useState<number | null>(null);
  const toggleManualImage = async (m: AdminModelView) => {
    setPendingLockId(m.id);
    try {
      await patchModel(m.id, { manualImage: !m.manualImage });
      await fetchPromoteSummary();
    } finally {
      setPendingLockId(null);
    }
  };

  const filteredModels = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();
    const ms = displayModels.filter((m) => {
      if (sourceFilter !== 'all' && m.source !== sourceFilter) return false;
      if (monetizationFilter !== 'all' && m.monetizationGroup !== monetizationFilter) return false;
      const isTrans = m.tags?.includes('trans');
      if (genderFilter === 'trans' && !isTrans) return false;
      if (genderFilter === 'straight' && isTrans) return false;
      const noImg = !hasPreview(m);
      const passActive = stateChips.has('active') && !m.blacklisted;
      const passBlack = stateChips.has('blacklist') && m.blacklisted;
      const passNoImg = stateChips.has('noimg') && noImg;
      if (stateChips.size > 0 && !(passActive || passBlack || passNoImg)) return false;
      if (search) {
        const hay = `${m.modelUsername} ${m.modelName ?? ''}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });
    return [...ms].sort((a, b) => {
      switch (sortOption) {
        case 'likes-asc': return (a.likesCount ?? 0) - (b.likesCount ?? 0);
        case 'username-asc': return a.modelUsername.localeCompare(b.modelUsername);
        case 'likes-desc':
        default: return (b.likesCount ?? 0) - (a.likesCount ?? 0);
      }
    });
  }, [displayModels, debouncedSearch, sourceFilter, monetizationFilter, genderFilter, stateChips, sortOption]);

  const visibleModels = useMemo(() => filteredModels.slice(0, visibleCount), [filteredModels, visibleCount]);

  // ---- Actions ----
  const toggleChip = (k: StateChip) => setStateChips(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const clearFilters = () => {
    setUrlState({ q: '', src: 'all', mon: 'all', gender: 'all' });
    setStateChips(new Set(['active']));
    setLocalSearchTerm('');
  };

  // Selecting a card means selecting the USERNAME: a dual-source card moves both of its
  // row ids in/out of selectedIds atomically, so bulk actions can never blacklist one
  // order and leave the sibling live in the public feed.
  const toggleSelect = (m: DisplayModel) => setSelectedIds(prev => {
    const n = new Set(prev);
    const ids = m.siblingEntry ? [m.id, m.siblingEntry.id] : [m.id];
    if (n.has(m.id)) ids.forEach(id => n.delete(id));
    else ids.forEach(id => n.add(id));
    return n;
  });

  // Selection counts are shown in CARDS (usernames), matching what the admin clicked —
  // a dual-source pair contributes two ids to selectedIds but counts as one card.
  const selectedCards = useMemo(
    () => displayModels.filter(m => selectedIds.has(m.id)),
    [displayModels, selectedIds]
  );

  const handleBulk = async (action: 'promote' | 'unpromote' | 'blacklist' | 'unblacklist') => {
    if (selectedCards.length === 0 || bulkLoading) return;
    const count = selectedCards.length;

    // Confirm destructive bulk actions
    const needsConfirm = action === 'blacklist' || action === 'unblacklist';
    if (needsConfirm) {
      const isBlacklist = action === 'blacklist';
      const ok = await confirm({
        title: isBlacklist
          ? `Blacklist ${count} ${count === 1 ? 'model' : 'models'}?`
          : `Remove blacklist from ${count} ${count === 1 ? 'model' : 'models'}?`,
        description: isBlacklist
          ? 'They will be hidden from public search and feeds. You can revert this from the Blacklist filter.'
          : 'They will become eligible for public search and feeds again.',
        confirmLabel: isBlacklist ? 'Blacklist' : 'Unblacklist',
        tone: isBlacklist ? 'danger' : 'warning',
        requireType: isBlacklist && count >= 10 ? 'BLACKLIST' : undefined
      });
      if (!ok) return;
    }

    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      const flags = action === 'promote' ? { promote: true }
        : action === 'unpromote' ? { promote: false }
        : action === 'blacklist' ? { blacklisted: true }
        : { blacklisted: false };
      await batchUpdateFlags(ids, flags);
      await fetchPromoteSummary();
      setSelectedIds(new Set());
    } catch (e) {
      console.error('Bulk action failed', e);
    } finally {
      setBulkLoading(false);
    }
  };

  // ---- Modal nav ----
  const editingIdx = useMemo(() =>
    editingModelId === null ? -1 : filteredModels.findIndex(m => m.id === editingModelId),
    [editingModelId, filteredModels]
  );

  const handlePrev = () => { if (editingIdx > 0) setEditingModelId(filteredModels[editingIdx - 1].id); };
  const handleNext = () => { if (editingIdx >= 0 && editingIdx < filteredModels.length - 1) setEditingModelId(filteredModels[editingIdx + 1].id); };
  const handleClose = () => { setEditingModelId(null); void fetchPromoteSummary(); };

  useEffect(() => {
    if (editingModelId === null) return;
    const k = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'ArrowLeft') { e.preventDefault(); handlePrev(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });

  const handleCardKey = (e: KeyboardEvent<HTMLAnchorElement>, m: AdminModelView) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingModelId(m.id); }
  };

  // ---- Render ----
  const subtitle = (
    <>
      <span className="hf-mono">{fmt(totalModels)}</span> total
      <span style={{ margin: '0 6px', color: 'var(--fg-4)' }}>·</span>
      <span className="hf-mono">{fmt(totalPromote)}</span> promoted
      <span style={{ margin: '0 6px', color: 'var(--fg-4)' }}>·</span>
      <span className="hf-mono">{fmt(blacklistCount)}</span> blacklisted
    </>
  );

  const actions = (
    <>
      <button className="hf-btn" title="Export visible as CSV"><Download size={14} /> Export</button>
      <button className="hf-btn primary" onClick={() => setCreateOpen(true)}>
        <Plus size={14} /> New model
      </button>
    </>
  );

  const singleSelectedId = selectedCards.length === 1 ? selectedCards[0].id : null;

  return (
    <>
      <PageHeader title="Models" subtitle={subtitle} actions={actions} />

      <div className="hf-content">
        {/* Pinpoint search */}
        <div className="hf-feature-box">
          <div className="hf-label-row" style={{ marginBottom: 8 }}>
            <span className="hf-label">Pinpoint search · finds any model in DB</span>
            <span className="hf-help" style={{ marginTop: 0 }}>↩ opens model detail · ignores filters below</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div className="hf-input" style={{ flex: 1 }}>
              <span className="hf-icon-l"><AtSign size={14} /></span>
              <input
                type="text"
                value={pinpointUsername}
                onChange={(e) => setPinpointUsername(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runPinpoint(); } }}
                placeholder="username"
              />
            </div>
            <button
              type="button"
              className="hf-btn primary"
              onClick={() => void runPinpoint()}
              disabled={pinpointSearching || pinpointUsername.trim().length === 0}
            >
              {pinpointSearching ? 'Searching…' : <>Find <ArrowRight size={14} /></>}
            </button>
          </div>
          {pinpointError && (
            <div className="hf-help" style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={12} /> {pinpointError}
            </div>
          )}
        </div>

        {/* KPI strip */}
        <div className="hf-kpi-strip">
          {!promoteSummary && (
            <>
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
            </>
          )}
          {promoteSummary && <>
          <Kpi label="Total"        value={fmt(totalModels)} />
          <Kpi label="OnlyFinder · OF"  value={fmt(sourceCounts.OF)} sub={promoteSummary.totalAllOF ? `of ${fmt(promoteSummary.totalAllOF)} in DB` : '—'} />
          <Kpi label="OnlyTraffic · OT" value={fmt(sourceCounts.OT)} sub={promoteSummary.totalAllOT ? `of ${fmt(promoteSummary.totalAllOT)} in DB` : '—'} />
          <Kpi label="Promoted"     value={fmt(totalPromote)} accent
               sub={`${monCounts.PPC} PPC · ${monCounts.CPL} CPL · ${monCounts.REVSHARE} RS`} />
          <Kpi label="Blacklisted"  value={fmt(blacklistCount)} />
          <Kpi label="No image"     value={fmt(noImgCount)} sub="needs covers" />
          </>}
        </div>

        {promoteSummary && promoteSummary.totalAll > 0 && (
          <div className="hf-help" style={{ marginTop: 4, color: 'var(--fg-3)', fontSize: 12 }}>
            DB totals (active, non-blacklisted):{' '}
            <span className="hf-mono">{fmt(promoteSummary.totalAll)}</span> total ·{' '}
            <span className="hf-mono">{fmt(promoteSummary.totalAllOT)}</span> OT (
            {Math.round(promoteSummary.totalAllOT / promoteSummary.totalAll * 100)}%) ·{' '}
            <span className="hf-mono">{fmt(promoteSummary.totalAllOF)}</span> OF (
            {Math.round(promoteSummary.totalAllOF / promoteSummary.totalAll * 100)}%) ·{' '}
            <span className="hf-mono">{fmt(promoteSummary.totalAll - promoteSummary.totalAllOT - promoteSummary.totalAllOF)}</span> other
            {' '}<span style={{ color: 'var(--fg-4)' }}>(grid above shows promoted only)</span>
          </div>
        )}

        {promoteSummary && (
          <div className="hf-help" style={{ marginTop: 2, color: 'var(--fg-3)', fontSize: 12 }}>
            Promoted active (promote=true, !blacklisted):{' '}
            <span title="OT total active-promoted">
              OT <span className="hf-mono">{fmt(promotedActiveMatrix.OT.total)}</span>
            </span>
            {' '}(PPC <span className="hf-mono">{fmt(promotedActiveMatrix.OT.PPC)}</span>
            {' '}· CPL <span className="hf-mono">{fmt(promotedActiveMatrix.OT.CPL)}</span>
            {' '}· REV <span className="hf-mono">{fmt(promotedActiveMatrix.OT.REVSHARE)}</span>)
            {' '}·{' '}
            <span title="OF total active-promoted">
              OF <span className="hf-mono">{fmt(promotedActiveMatrix.OF.total)}</span>
            </span>
            {' '}(PPC <span className="hf-mono">{fmt(promotedActiveMatrix.OF.PPC)}</span>
            {' '}· CPL <span className="hf-mono">{fmt(promotedActiveMatrix.OF.CPL)}</span>
            {' '}· REV <span className="hf-mono">{fmt(promotedActiveMatrix.OF.REVSHARE)}</span>)
            {' '}
            <button
              type="button"
              className="hf-btn ghost sm"
              style={{ marginLeft: 6, padding: '0 6px', fontSize: 11 }}
              onClick={() => void fetchPromoteSummary()}
              disabled={isSummaryLoading}
              title="Refresh after a sync"
            >
              <RefreshCw size={11} /> refresh
            </button>
          </div>
        )}

        {/* Filter toolbar */}
        <div className="hf-card">
          <div className="hf-card-body tight">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div className="hf-search" style={{ flex: '1 1 280px' }}>
                <Search size={14} />
                <input
                  type="search"
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  placeholder="Search by username or name"
                  aria-label="Search models"
                />
                {localSearchTerm && (
                  <button
                    type="button"
                    className="hf-btn ghost"
                    style={{ padding: 4, boxShadow: 'none', borderColor: 'transparent', color: 'var(--fg-3)' }}
                    onClick={() => setLocalSearchTerm('')}
                    aria-label="Clear search"
                    title="Clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <span className="hf-count">
                {fmt(filteredModels.length)} of {fmt(totalModels)} visible
              </span>
              <div className="hf-segmented" role="group" aria-label="View mode" style={{ marginLeft: 'auto' }}>
                <button className={viewMode === 'grid' ? 'on' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={13} /> Grid</button>
                <button className={viewMode === 'table' ? 'on' : ''} onClick={() => setViewMode('table')}><List size={13} /> Table</button>
              </div>
            </div>

            <div className="hf-divider dashed" style={{ margin: '12px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <FilterLabel>Source</FilterLabel>
              <div className="hf-segmented">
                <Seg on={sourceFilter === 'all'} onClick={() => setSourceFilter('all')}>any <span className="ct">{fmt(totalModels)}</span></Seg>
                <Seg on={sourceFilter === 'OF'} onClick={() => setSourceFilter('OF')}>OF <span className="ct">{fmt(sourceCounts.OF)}</span></Seg>
                <Seg on={sourceFilter === 'OT'} onClick={() => setSourceFilter('OT')}>OT <span className="ct">{fmt(sourceCounts.OT)}</span></Seg>
              </div>

              <FilterLabel>Monetisation</FilterLabel>
              <div className="hf-segmented">
                <Seg on={monetizationFilter === 'all'} onClick={() => setMonetizationFilter('all')}>any</Seg>
                <Seg on={monetizationFilter === 'PPC'} onClick={() => setMonetizationFilter('PPC')}>PPC <span className="ct">{fmt(monCounts.PPC)}</span></Seg>
                <Seg on={monetizationFilter === 'CPL'} onClick={() => setMonetizationFilter('CPL')}>CPL <span className="ct">{fmt(monCounts.CPL)}</span></Seg>
                <Seg on={monetizationFilter === 'REVSHARE'} onClick={() => setMonetizationFilter('REVSHARE')}>REV <span className="ct">{fmt(monCounts.REVSHARE)}</span></Seg>
              </div>

              <FilterLabel>Gender</FilterLabel>
              <div className="hf-segmented">
                <Seg on={genderFilter === 'all'} onClick={() => setGenderFilter('all')}>any</Seg>
                <Seg on={genderFilter === 'straight'} onClick={() => setGenderFilter('straight')}>straight <span className="ct">{fmt(genderCounts.straight)}</span></Seg>
                <Seg on={genderFilter === 'trans'} onClick={() => setGenderFilter('trans')}>trans <span className="ct">{fmt(genderCounts.trans)}</span></Seg>
              </div>

              <FilterLabel>State</FilterLabel>
              <div style={{ display: 'flex', gap: 6 }}>
                <Chip on={stateChips.has('active')} onClick={() => toggleChip('active')}>active <span className="ct">{fmt(totalModels - blacklistCount)}</span></Chip>
                <Chip on={stateChips.has('blacklist')} onClick={() => toggleChip('blacklist')}>blacklist <span className="ct">{fmt(blacklistCount)}</span></Chip>
                <Chip on={stateChips.has('noimg')} onClick={() => toggleChip('noimg')}>no img <span className="ct">{fmt(noImgCount)}</span></Chip>
              </div>

              <select className="hf-select" style={{ maxWidth: 170, marginLeft: 'auto' }}
                      value={sortOption} onChange={(e) => setSortOption(e.target.value as typeof sortOption)}>
                <option value="likes-desc">sort: likes ↓</option>
                <option value="likes-asc">sort: likes ↑</option>
                <option value="username-asc">sort: A → Z</option>
              </select>
              <button className="hf-btn ghost sm" onClick={clearFilters}>clear all</button>
            </div>
          </div>
        </div>

        {/* Error / loading */}
        {summaryError && (
          <ErrorBanner
            kind="danger"
            title="Failed to load models"
            message={summaryError}
            onRetry={() => void fetchPromoteSummary()}
          />
        )}

        {isSummaryLoading && !promoteSummary && !summaryError && (
          viewMode === 'grid'
            ? <CardSkeleton count={12} height={220} />
            : (
              <div className="hf-card flush">
                <table className="hf-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th style={{ width: 44 }}>img</th>
                      <th>@username</th>
                      <th>name</th>
                      <th>src</th>
                      <th>mon</th>
                      <th className="num">likes</th>
                      <th className="num">score</th>
                      <th>flags</th>
                      <th>updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rows={8}
                      columns={[4, 6, 16, 16, 6, 8, 8, 8, 14, 10]}
                      numericColumns={[6, 7]}
                    />
                  </tbody>
                </table>
              </div>
            )
        )}

        {/* Empty state */}
        {!isSummaryLoading && filteredModels.length === 0 && promoteSummary && (
          <EmptyState
            Icon={debouncedSearch || stateChips.size !== 1 || sourceFilter !== 'all' || monetizationFilter !== 'all' || genderFilter !== 'all' ? FilterX : Users}
            title="No models match these filters"
            description="Try widening the source, monetisation, or state filters — or clear the search to see everything."
            action={
              <button className="hf-btn" onClick={clearFilters}>
                <RefreshCw size={13} /> Clear filters
              </button>
            }
          />
        )}

        {viewMode === 'grid' && filteredModels.length > 0 && (
          <div className="hf-model-grid">
            {visibleModels.map(m => {
              const url = getPreviewUrl(m);
              const isSel = selectedIds.has(m.id);
              const initials = (m.modelName || m.modelUsername || '?').trim().charAt(0).toUpperCase();
              return (
                <a
                  key={m.id}
                  className={`hf-model-card ${isSel ? 'selected' : ''}`}
                  href={`/models/${m.id}/edit`}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.preventDefault(); setEditingModelId(m.id); }}
                  onKeyDown={(e) => handleCardKey(e, m)}
                >
                  <div className="hf-model-card-cover">
                    {url
                      ? <img className="hf-cover" src={url} alt={m.modelUsername} />
                      : <div className="hf-imgph" style={{ fontSize: 18 }}>{initials}</div>}
                    <div className="hf-model-card-overlay-tl">
                      {m.source && <span className="hf-tag solid sm">{m.source}</span>}
                      {m.monetizationGroup && (
                        <span className={`hf-tag sm ${m.monetizationGroup === 'PPC' ? 'accent' : m.monetizationGroup === 'CPL' ? 'success' : 'warning'}`}>
                          {m.monetizationGroup === 'REVSHARE' ? 'REV' : m.monetizationGroup}
                        </span>
                      )}
                      {m.blacklisted && <span className="hf-tag danger sm">BL</span>}
                    </div>
                    <div
                      className="hf-model-card-overlay-tr"
                      onClick={(e) => e.preventDefault()}
                    >
                      <span
                        className={`hf-overlay-check ${isSel ? 'on' : ''}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(m); }}
                        role="checkbox"
                        aria-checked={isSel}
                        aria-label={`Select ${m.modelUsername}`}
                      >
                        {isSel && <Check size={12} />}
                      </span>
                    </div>
                  </div>
                  <div className="hf-model-card-body">
                    <div className="hf-model-card-handle">@{m.modelUsername}</div>
                    {m.modelName && <div className="hf-model-card-name">{m.modelName}</div>}
                    <div className="hf-model-card-meta">
                      <span className="hf-mono">♡ {fmt(m.likesCount)}</span>
                      <span style={{ color: 'var(--fg-3)' }}>{formatRelativeTime(m.updatedAt)}</span>
                      {typeof m.finalScore === 'number' && (
                        <span
                          className="hf-mono"
                          title="Final score"
                          style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 600 }}
                        >
                          {m.finalScore.toFixed(1)}
                        </span>
                      )}
                    </div>
                    {(m.promote || m.dmcaBlocked || m.isPromo) && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                        {m.promote     && <span className="hf-tag accent sm">promote</span>}
                        {m.dmcaBlocked && <span className="hf-tag warning sm">dmca</span>}
                        {m.isPromo     && <span className="hf-tag outline sm">isPromo</span>}
                      </div>
                    )}
                    {m.siblingEntry && (
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SourceToggle
                          active={activeSourceByUsername.get(m.modelUsername.toLowerCase()) ?? 'OF'}
                          busy={pendingSourceUsername === m.modelUsername}
                          onSelect={(s) => void handleSetActive(m.modelUsername, s)}
                        />
                        <button
                          type="button"
                          className="hf-btn icon sm"
                          disabled={pendingLockId === m.id}
                          onClick={() => void toggleManualImage(m)}
                          title={m.manualImage ? 'Photo locked — sync will not overwrite it' : 'Photo auto-updates from sync'}
                        >
                          {m.manualImage ? <Lock size={12} /> : <Unlock size={12} />}
                        </button>
                      </div>
                    )}
                  </div>
                </a>
              );
            })}
            {visibleCount < filteredModels.length && <div ref={sentinelRef} style={{ gridColumn: '1 / -1', height: 10 }} />}
          </div>
        )}

        {viewMode === 'table' && filteredModels.length > 0 && (
          <div className="hf-card flush">
            <table className="hf-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th style={{ width: 44 }}>img</th>
                  <th>@username</th>
                  <th>name</th>
                  <th>src</th>
                  <th>mon</th>
                  <th className="num">likes</th>
                  <th className="num">score</th>
                  <th>flags</th>
                  <th>updated</th>
                </tr>
              </thead>
              <tbody>
                {visibleModels.map(m => {
                  const url = getPreviewUrl(m);
                  const sel = selectedIds.has(m.id);
                  return (
                    <tr key={m.id} className={sel ? 'selected' : ''} onClick={() => setEditingModelId(m.id)} style={{ cursor: 'pointer' }}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span
                          className={`hf-overlay-check ${sel ? 'on' : ''}`}
                          onClick={() => toggleSelect(m)}
                        >{sel && <Check size={12} />}</span>
                      </td>
                      <td>
                        <div style={{ width: 32, height: 32, borderRadius: 4, overflow: 'hidden', background: 'var(--surface-2)' }}>
                          {url
                            ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <div className="hf-imgph" style={{ width: '100%', height: '100%' }} />}
                        </div>
                      </td>
                      <td><span className="hf-mono">@{m.modelUsername}</span></td>
                      <td style={{ color: 'var(--fg-2)' }}>{m.modelName || '—'}</td>
                      <td>{m.source && <span className="hf-tag outline sm">{m.source}</span>}</td>
                      <td>{m.monetizationGroup && <span className={`hf-tag sm ${m.monetizationGroup === 'PPC' ? 'accent' : m.monetizationGroup === 'CPL' ? 'success' : 'warning'}`}>{m.monetizationGroup === 'REVSHARE' ? 'REV' : m.monetizationGroup}</span>}</td>
                      <td className="num">{fmt(m.likesCount)}</td>
                      <td className="num">{m.finalScore?.toFixed(1) ?? '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          {m.promote && <span className="hf-tag accent sm">promo</span>}
                          {m.blacklisted && <span className="hf-tag danger sm">BL</span>}
                          {m.dmcaBlocked && <span className="hf-tag warning sm">DMCA</span>}
                          {m.isPromo && <span className="hf-tag outline sm">isPromo</span>}
                          {m.siblingEntry && (
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SourceToggle
                                active={activeSourceByUsername.get(m.modelUsername.toLowerCase()) ?? 'OF'}
                                busy={pendingSourceUsername === m.modelUsername}
                                onSelect={(s) => void handleSetActive(m.modelUsername, s)}
                              />
                              <button
                                type="button"
                                className="hf-btn icon sm"
                                disabled={pendingLockId === m.id}
                                onClick={() => void toggleManualImage(m)}
                                title={m.manualImage ? 'Photo locked — sync will not overwrite it' : 'Photo auto-updates from sync'}
                              >
                                {m.manualImage ? <Lock size={12} /> : <Unlock size={12} />}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ color: 'var(--fg-3)', fontSize: 12 }}>{formatRelativeTime(m.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visibleCount < filteredModels.length && <div ref={sentinelRef} style={{ height: 10 }} />}
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="hf-bulk-bar" role="region" aria-label="Bulk actions">
          <span className="hf-mono" style={{ fontSize: 13 }}>{selectedCards.length} selected</span>
          <span className="hf-bulk-divider" />
          <button onClick={() => handleBulk('promote')} disabled={bulkLoading}><Star size={13} /> Promote</button>
          <button onClick={() => handleBulk('blacklist')} disabled={bulkLoading}><Slash size={13} /> Blacklist</button>
          <button onClick={() => handleBulk('unblacklist')} disabled={bulkLoading}><RefreshCw size={13} /> Unblacklist</button>
          {singleSelectedId !== null && (
            <button onClick={() => setEditingModelId(singleSelectedId)}><Edit2 size={13} /> Edit</button>
          )}
          <span className="hf-bulk-divider" />
          <button onClick={() => setSelectedIds(new Set())}><X size={13} /> Clear</button>
        </div>
      )}

      {/* Create model modal */}
      <CreateModelForm
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => { void fetchPromoteSummary(); }}
      />

      {/* Edit modal (renders its own .me-overlay) */}
      {editingModelId !== null && (
        <>
          <ModelEditPage modelId={editingModelId} onClose={handleClose} />
          {filteredModels.length > 1 && (
            <nav
              aria-label="Model navigation"
              style={{
                position: 'fixed', bottom: 24, right: 24, display: 'flex',
                alignItems: 'center', gap: 4,
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-2)', padding: 4,
                boxShadow: 'var(--shadow-lg)',
                zIndex: 'var(--z-popover, 1100)'
              }}
            >
              <button
                type="button"
                className="hf-btn icon"
                onClick={handlePrev}
                disabled={editingIdx <= 0}
                title="Previous (⌘←)"
                aria-label="Previous model"
              >‹</button>
              <span
                style={{ padding: '0 10px', fontSize: 12, color: 'var(--fg-3)' }}
                className="hf-mono"
                aria-live="polite"
                aria-atomic="true"
              >
                {editingIdx + 1} / {filteredModels.length}
              </span>
              <button
                type="button"
                className="hf-btn icon"
                onClick={handleNext}
                disabled={editingIdx >= filteredModels.length - 1}
                title="Next (⌘→)"
                aria-label="Next model"
              >›</button>
            </nav>
          )}
        </>
      )}
    </>
  );
};

// --- Helpers ---

const Kpi: React.FC<{ label: string; value: string; sub?: React.ReactNode; accent?: boolean }> = ({ label, value, sub, accent }) => (
  <div className={`hf-kpi ${accent ? 'accent' : ''}`}>
    <span className="hf-kpi-label">{label}</span>
    <span className="hf-kpi-value">{value}</span>
    {sub && <span className="hf-kpi-sub">{sub}</span>}
  </div>
);

const FilterLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="hf-label" style={{ marginLeft: 4 }}>{children}</span>
);

const Seg: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <button className={on ? 'on' : ''} onClick={onClick}>{children}</button>
);

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <button type="button" className={`hf-chip ${on ? 'on' : ''}`} onClick={onClick}>{children}</button>
);

export default ModelsListPage;
